/**
 * Types for the Broker Comparison Tool.
 *
 * `RawBroker` matches the response shape from
 *   GET /functions/v1/public-brokers?lang=...
 * `Broker` is our normalized client-friendly version with parsed numeric fields,
 * derived courtage tier, and language-aware FI license detection.
 */

export interface RawBroker {
  id: string;
  team_id: string;
  title: string;
  tagline: string | null;
  logo_url: string | null;
  language: string;
  year_created: number | null;
  sort_order: number | null;

  fee_value: string | null;
  fee_description: string | null;
  courtage_value: string | null;
  courtage_description: string | null;
  min_deposit_value: string | null;
  min_deposit_description: string | null;
  exchange_fee_value: string | null;
  exchange_fee_description: string | null;
  app_rating_value: string | null;
  app_rating_description: string | null;
  savings_interest_value: string | null;
  savings_interest_description: string | null;

  key_features: string[] | null;
  key_downsides: string[] | null;

  categories: string[] | null;
  focus: string[] | null;
  markets: string[] | null;
  integrations: string[] | null;

  demo_account: boolean | null;
  education: boolean | null;
  fractional_trading: boolean | null;
  savings_interest: boolean | null;
  securities_lending: boolean | null;
  auto_tax_reporting: boolean | null;
  copy_trading: boolean | null;
  bank_id: boolean | null;

  security_text: string | null;
  tax_text: string | null;
  offer_text: string | null;
  review_text: string | null;
  affiliate_cta_url: string | null;
  affiliate_cta_label: string | null;
  review_cta_url: string | null;
  review_cta_label: string | null;

  created_at?: string;
  updated_at?: string;
}

export type CourtageTier = 'low' | 'medium' | 'high';

export interface Broker {
  id: string;
  title: string;
  tagline: string;
  logoUrl: string | null;
  yearCreated: number | null;

  feeValue: string;
  feeDescription: string;
  courtageValue: string;
  courtageDescription: string;
  minDepositValue: string;
  minDepositDescription: string;
  exchangeFeeValue: string;
  exchangeFeeDescription: string;
  appRatingValue: string;
  appRatingDescription: string;
  savingsInterestValue: string;
  savingsInterestDescription: string;
  hasSavingsInterest: boolean;

  // Parsed numeric fields used by sliders / sorting
  courtageTier: CourtageTier;
  courtageTierRank: number; // 1 = low, 2 = medium, 3 = high (for ascending sort)
  minDepositNumber: number;
  exchangeFeePercent: number;
  appRatingNumber: number;
  savingsInterestPercent: number;

  keyFeatures: string[];
  keyDownsides: string[];
  accountTypes: string[];
  productTypes: string[];
  focusIds: string[];

  fiLicensed: boolean;
  autoTaxReporting: boolean;
  bankId: boolean;
  hasOffer: boolean;
  integrationIds: string[];
  securityText: string;
  taxText: string;
  offerText: string;
  reviewText: string;
  affiliateCtaUrl: string | null;
  affiliateCtaLabel: string;
  reviewCtaUrl: string | null;
  reviewCtaLabel: string;

  /** ISO date strings if available — used for Review structured data. */
  createdAt: string | null;
  updatedAt: string | null;
}

export type SortKey =
  | 'default'
  | 'lowestCourtage'
  | 'lowestDeposit'
  | 'lowestFx'
  | 'highestRating'
  | 'highestSavings';

export const SORT_KEYS: SortKey[] = [
  'default',
  'lowestCourtage',
  'lowestDeposit',
  'lowestFx',
  'highestRating',
  'highestSavings',
];

export type FilterState = {
  sortBy: SortKey;
  maxDeposit: number;
  maxFx: number;
  accounts: string[];
  products: string[];
  /** Empty string = all focus areas. */
  focus: string;
  fiOnly: boolean;
  autoTaxOnly: boolean;
  hasOfferOnly: boolean;
  bankIdOnly: boolean;
  /** Selected integration ids (AND-match like products). */
  integrations: string[];
};

export interface SliderBounds {
  min: number;
  max: number;
  step: number;
  /** True when min === max — slider would be useless and should be hidden. */
  degenerate: boolean;
}

export const COURTAGE_TIER_RANK: Record<CourtageTier, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

// Account types we surface as filterable checkboxes.
// Values are matched against the `categories` array from the API.
export const ACCOUNT_TYPE_OPTIONS = [
  { id: 'ISK', label: 'ISK', match: ['ISK'] },
  { id: 'KF', label: 'KF', match: ['KF'] },
  { id: 'Depå', label: 'Depå', match: ['Depå', 'Depa'] },
  { id: 'Tjänstepension', label: 'Tjänstepension', match: ['Tjänstepension', 'Pension', 'Pensjonskonto'] },
] as const;

// Investment focus / profile checkboxes.
// Matched against the `focus` array from the API.
// Labels are taken from translations; only the canonical id is stable.
export const FOCUS_OPTIONS = [
  {
    id: 'longTerm',
    match: ['Långsiktigt sparande', 'Long-term saving', 'Long-term savings', 'Langsiktig sparing'],
  },
  {
    id: 'trading',
    match: ['Trading', 'Day trading'],
  },
] as const;

// Integration checkboxes (matched against the `integrations` array from the API).
export const INTEGRATION_OPTIONS = [
  { id: 'API', label: 'API', match: ['API'] },
  { id: 'TradingView', label: 'TradingView', match: ['TradingView', 'Trading View'] },
  { id: 'MetaTrader', label: 'MetaTrader', match: ['MetaTrader', 'Meta Trader', 'MT4', 'MT5'] },
] as const;

// Product / asset class checkboxes.
export const PRODUCT_OPTIONS = [
  { id: 'aktier', label: 'Aktier', match: ['Aktier', 'Stocks', 'Aksjer'] },
  { id: 'fonder', label: 'Fonder', match: ['Fonder', 'Funds', 'Fond'] },
  { id: 'ETF', label: 'ETF', match: ['ETF', 'ETF:er', 'ETFs'] },
  { id: 'CFD', label: 'CFD', match: ['CFD'] },
  { id: 'krypto', label: 'Krypto', match: ['Krypto', 'Crypto'] },
  { id: 'råvaror', label: 'Råvaror', match: ['Råvaror', 'Råvarer', 'Commodities'] },
] as const;
